/**
 * 預處理 Canvas
 *
 * 提供圖片預處理功能：
 * - 參考線繪製（用於比例尺校準）
 * - 橡皮擦（覆蓋矩形）
 * - 塗黑線條（封閉區域）
 */

import { BaseCanvas, type CanvasOptions } from './base-canvas';
import { drawLine, fillCircle, fillRect, drawRect, drawLabel } from './drawing-utils';
import { distance } from '@/utils/geometry';
import {
  preprocessStore,
  setDrawingMode,
  startDrawingReferenceLine,
  finishReferenceLine,
  cancelReferenceLine,
  addCoverRectangle,
  addBlackLine,
  selectDimension,
  startDraggingEndpoint,
  stopDraggingEndpoint,
  updateSelectedDimensionEndpoint,
  type DrawingMode,
  type EndpointType,
} from '@/store/preprocess-store';
import type { Point, Rectangle, DetectedDimension } from '@/types';

// ========================================================================
// 型別定義
// ========================================================================

export interface PreprocessCanvasOptions extends CanvasOptions {
  /** 當參考線完成時的回呼（用於請求長度輸入） */
  onReferenceLineComplete?: (pixelLength: number) => Promise<number | null>;
}

// ========================================================================
// PreprocessCanvas 類別
// ========================================================================

export class PreprocessCanvas extends BaseCanvas {
  /** 當前繪圖模式 */
  private drawingMode: DrawingMode = 'none';

  /** 是否正在繪製 */
  private isDrawing: boolean = false;

  /** 繪製起點 */
  private startPoint: Point | null = null;

  /** 當前滑鼠位置 */
  private currentPoint: Point | null = null;

  /** 參考線完成回呼 */
  private onReferenceLineComplete?: (pixelLength: number) => Promise<number | null>;

  /** Store 取消訂閱函式 */
  private unsubscribe: (() => void) | null = null;

  /** 鍵盤事件處理函式（用於取消訂閱） */
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  /** 滑鼠懸停的尺寸 ID */
  private hoveredDimensionId: number | null = null;

  /** 滑鼠懸停的端點 */
  private hoveredEndpoint: EndpointType | null = null;

  /** 端點拖曳把手半徑（像素） */
  private readonly HANDLE_RADIUS = 8;

  /** 線段點擊容差（像素） */
  private readonly LINE_HIT_TOLERANCE = 10;

  constructor(options: PreprocessCanvasOptions) {
    super(options);
    this.onReferenceLineComplete = options.onReferenceLineComplete;
  }

  // ========================================================================
  // 初始化
  // ========================================================================

  initialize(): void {
    this.bindPointerEvents({
      onPointerDown: this.onPointerDown.bind(this),
      onPointerMove: this.onPointerMove.bind(this),
      onPointerUp: this.onPointerUp.bind(this),
    });

    // 啟用滾輪縮放
    this.enableWheelZoom();

    // 訂閱 Store 變更
    this.unsubscribe = preprocessStore.subscribe((state) => {
      // 如果模式改變，清除進行中的繪製狀態
      if (this.drawingMode !== state.drawingMode) {
        this.startPoint = null;
        this.currentPoint = null;
        this.isDrawing = false;
      }
      this.drawingMode = state.drawingMode;
      this.updateCursor();
      this.render();
    });

    // 取得初始狀態
    const state = preprocessStore.getState();
    this.drawingMode = state.drawingMode;
    this.updateCursor();

    // 監聽 Escape 鍵取消正在進行的繪製
    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.startPoint) {
        this.startPoint = null;
        this.currentPoint = null;
        this.isDrawing = false;
        this.render();
      }
    };
    window.addEventListener('keydown', this.keydownHandler);
  }

  // ========================================================================
  // 游標更新
  // ========================================================================

  private updateCursor(): void {
    // 如果正在拖曳端點，顯示移動游標
    const state = preprocessStore.getState();
    if (state.draggingEndpoint) {
      this.canvas.style.cursor = 'grabbing';
      return;
    }

    // 如果懸停在端點上，顯示抓取游標
    if (this.hoveredEndpoint) {
      this.canvas.style.cursor = 'grab';
      return;
    }

    // 如果在尺寸選取模式且懸停在尺寸上
    if (this.drawingMode === 'dimension-select' && this.hoveredDimensionId !== null) {
      this.canvas.style.cursor = 'pointer';
      return;
    }

    switch (this.drawingMode) {
      case 'reference':
        this.canvas.style.cursor = 'crosshair';
        break;
      case 'eraser':
        this.canvas.style.cursor = 'cell';
        break;
      case 'blackline':
        this.canvas.style.cursor = 'crosshair';
        break;
      case 'dimension-select':
        this.canvas.style.cursor = 'default';
        break;
      default:
        this.canvas.style.cursor = 'default';
    }
  }

  // ========================================================================
  // 事件處理
  // ========================================================================

  private onPointerDown(point: Point, _event: PointerEvent): void {
    const state = preprocessStore.getState();

    // 處理尺寸選取模式
    if (this.drawingMode === 'dimension-select') {
      // 如果已有選中的尺寸且點擊在端點上，開始拖曳
      if (state.selectedDimensionId !== null && this.hoveredEndpoint) {
        startDraggingEndpoint(this.hoveredEndpoint);
        return;
      }

      // 如果點擊在尺寸線段上，選取該尺寸
      const hitDimension = this.hitTestDimension(point);
      if (hitDimension) {
        selectDimension(hitDimension.id);
      } else {
        // 點擊空白處取消選取
        selectDimension(null);
      }
      this.render();
      return;
    }

    if (this.drawingMode === 'none') return;

    // 參考線模式：使用兩次點擊的方式
    if (this.drawingMode === 'reference') {
      if (!this.startPoint) {
        // 第一次點擊：設定起點
        this.startPoint = point;
        this.currentPoint = point;
        startDrawingReferenceLine(point);
        this.render();
      } else {
        // 第二次點擊：完成參考線
        this.completeReferenceLine(this.startPoint, point);
        this.startPoint = null;
        this.currentPoint = null;
      }
      return;
    }

    // 塗黑線條模式：使用兩次點擊的方式
    if (this.drawingMode === 'blackline') {
      if (!this.startPoint) {
        // 第一次點擊：設定起點
        this.startPoint = point;
        this.currentPoint = point;
        this.render();
      } else {
        // 第二次點擊：完成線段
        this.completeBlackLine(this.startPoint, point);
        this.startPoint = null;
        this.currentPoint = null;
        this.render();
      }
      return;
    }

    // 橡皮擦模式：使用拖曳方式
    this.isDrawing = true;
    this.startPoint = point;
    this.currentPoint = point;
  }

  private onPointerMove(point: Point, _event: PointerEvent): void {
    const state = preprocessStore.getState();

    // 處理端點拖曳
    if (state.draggingEndpoint) {
      updateSelectedDimensionEndpoint(state.draggingEndpoint, point);
      this.render();
      return;
    }

    // 處理尺寸選取模式的懸停檢測
    if (this.drawingMode === 'dimension-select') {
      // 先檢查是否懸停在選中尺寸的端點上
      if (state.selectedDimensionId !== null) {
        const hoveredEndpoint = this.hitTestEndpoint(point, state.selectedDimensionId);
        if (hoveredEndpoint !== this.hoveredEndpoint) {
          this.hoveredEndpoint = hoveredEndpoint;
          this.updateCursor();
          this.render();
        }
      } else {
        this.hoveredEndpoint = null;
      }

      // 檢查是否懸停在尺寸線段上
      const hitDimension = this.hitTestDimension(point);
      const newHoveredId = hitDimension?.id ?? null;
      if (newHoveredId !== this.hoveredDimensionId) {
        this.hoveredDimensionId = newHoveredId;
        this.updateCursor();
        this.render();
      }
      return;
    }

    // 參考線與塗黑線條模式：有起點時即時更新預覽
    if ((this.drawingMode === 'reference' || this.drawingMode === 'blackline') && this.startPoint) {
      this.currentPoint = point;
      this.render();
      return;
    }

    if (!this.isDrawing) return;
    this.currentPoint = point;
    this.render();
  }

  private async onPointerUp(point: Point, _event: PointerEvent): Promise<void> {
    const state = preprocessStore.getState();

    // 處理端點拖曳結束
    if (state.draggingEndpoint) {
      stopDraggingEndpoint();
      this.render();
      return;
    }

    // 參考線與塗黑線條模式使用點擊兩次方式，不在 pointerUp 處理
    if (this.drawingMode === 'reference' || this.drawingMode === 'blackline') return;

    if (!this.isDrawing || !this.startPoint) return;
    this.isDrawing = false;

    // 目前只有橡皮擦模式使用拖曳方式
    if (this.drawingMode === 'eraser') {
      this.completeCoverRectangle(this.startPoint, point);
    }

    this.startPoint = null;
    this.currentPoint = null;
    this.render();
  }

  // ========================================================================
  // 碰撞檢測
  // ========================================================================

  /**
   * 檢測點擊是否在某個尺寸線段上
   */
  private hitTestDimension(point: Point): DetectedDimension | null {
    const state = preprocessStore.getState();
    const tolerance = this.LINE_HIT_TOLERANCE / this.scale;

    for (const dim of state.detectedDimensions) {
      if (!dim.line) continue;

      const dist = this.pointToLineDistance(point, dim.line.start, dim.line.end);
      if (dist <= tolerance) {
        // 確保點在線段範圍內（而不是線段延長線上）
        if (this.isPointOnLineSegment(point, dim.line.start, dim.line.end, tolerance)) {
          return dim;
        }
      }
    }

    return null;
  }

  /**
   * 檢測點擊是否在選中尺寸的端點上
   */
  private hitTestEndpoint(point: Point, dimensionId: number): EndpointType | null {
    const state = preprocessStore.getState();
    const dimension = state.detectedDimensions.find((d) => d.id === dimensionId);

    if (!dimension?.line) return null;

    const handleRadius = this.HANDLE_RADIUS / this.scale;

    // 檢查起點
    if (distance(point, dimension.line.start) <= handleRadius) {
      return 'start';
    }

    // 檢查終點
    if (distance(point, dimension.line.end) <= handleRadius) {
      return 'end';
    }

    return null;
  }

  /**
   * 計算點到線段的距離
   */
  private pointToLineDistance(point: Point, lineStart: Point, lineEnd: Point): number {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    const lengthSquared = dx * dx + dy * dy;

    if (lengthSquared === 0) {
      return distance(point, lineStart);
    }

    // 投影參數 t
    const t = Math.max(0, Math.min(1, ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSquared));

    // 投影點
    const projX = lineStart.x + t * dx;
    const projY = lineStart.y + t * dy;

    return distance(point, { x: projX, y: projY });
  }

  /**
   * 檢查點是否在線段範圍內
   */
  private isPointOnLineSegment(point: Point, lineStart: Point, lineEnd: Point, tolerance: number): boolean {
    const minX = Math.min(lineStart.x, lineEnd.x) - tolerance;
    const maxX = Math.max(lineStart.x, lineEnd.x) + tolerance;
    const minY = Math.min(lineStart.y, lineEnd.y) - tolerance;
    const maxY = Math.max(lineStart.y, lineEnd.y) + tolerance;

    return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
  }

  // ========================================================================
  // 繪製完成處理
  // ========================================================================

  /**
   * 完成參考線繪製
   */
  private async completeReferenceLine(start: Point, end: Point): Promise<void> {
    const pixelLength = distance(start, end);

    // 最小長度檢查
    if (pixelLength < 20) {
      this.render();
      return;
    }

    // 請求使用者輸入實際長度
    if (this.onReferenceLineComplete) {
      const meters = await this.onReferenceLineComplete(pixelLength);
      if (meters !== null && meters > 0) {
        finishReferenceLine(end, meters);
      } else {
        cancelReferenceLine();
      }
    } else {
      // 預設使用 1 公尺
      finishReferenceLine(end, 1);
    }

    // 完成後切換回無模式
    setDrawingMode('none');
  }

  /**
   * 完成覆蓋矩形
   */
  private completeCoverRectangle(start: Point, end: Point): void {
    const rect: Rectangle = {
      x: Math.min(start.x, end.x),
      y: Math.min(start.y, end.y),
      width: Math.abs(end.x - start.x),
      height: Math.abs(end.y - start.y),
    };

    // 最小尺寸檢查
    if (rect.width > 5 && rect.height > 5) {
      addCoverRectangle(rect);
    }
  }

  /**
   * 完成塗黑線條
   */
  private completeBlackLine(start: Point, end: Point): void {
    const length = distance(start, end);

    // 最小長度檢查
    if (length > 10) {
      addBlackLine({ start, end });
    }
  }

  // ========================================================================
  // 渲染
  // ========================================================================

  render(): void {
    this.clear();
    this.drawImage();

    const state = preprocessStore.getState();

    this.beginImageCoords();

    // 繪製覆蓋矩形（橡皮擦效果）
    for (const rect of state.coverRectangles) {
      fillRect(this.ctx, rect, { color: '#ffffff' });
    }

    // 繪製塗黑線條
    for (const line of state.blackLines) {
      drawLine(this.ctx, line.start, line.end, {
        color: '#000000',
        width: 8 / this.scale,
        cap: 'round',
      });
    }

    // 繪製參考線
    if (state.referenceLine) {
      this.drawReferenceLine(state.referenceLine.start, state.referenceLine.end, state.referenceLine.lengthMeters);
    }

    // 繪製預覽
    // 參考線與塗黑線條模式：只要有起點就顯示預覽（點擊兩次模式）
    // 橡皮擦模式：需要在拖曳狀態才顯示預覽
    const shouldDrawPreview =
      this.startPoint &&
      this.currentPoint &&
      (this.drawingMode === 'reference' || this.drawingMode === 'blackline' || this.isDrawing);
    if (shouldDrawPreview && this.startPoint && this.currentPoint) {
      this.drawPreview(this.startPoint, this.currentPoint);
    }

    // 繪製偵測到的尺寸
    for (const dim of state.detectedDimensions) {
      this.drawDetectedDimension(dim);
    }

    this.endImageCoords();
  }

  /**
   * 繪製參考線（含箭頭和標籤）
   */
  private drawReferenceLine(start: Point, end: Point, meters: number): void {
    const color = '#3b82f6';
    const lineWidth = 3 / this.scale;

    // 繪製主線條
    drawLine(this.ctx, start, end, { color, width: lineWidth });

    // 繪製端點箭頭
    const arrowSize = 10 / this.scale;
    const angle = Math.atan2(end.y - start.y, end.x - start.x);

    // 起點箭頭
    this.ctx.save();
    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(
      start.x + arrowSize * Math.cos(angle - Math.PI / 6),
      start.y + arrowSize * Math.sin(angle - Math.PI / 6)
    );
    this.ctx.lineTo(
      start.x + arrowSize * Math.cos(angle + Math.PI / 6),
      start.y + arrowSize * Math.sin(angle + Math.PI / 6)
    );
    this.ctx.closePath();
    this.ctx.fill();
    this.ctx.restore();

    // 終點箭頭
    this.ctx.save();
    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.moveTo(end.x, end.y);
    this.ctx.lineTo(
      end.x - arrowSize * Math.cos(angle - Math.PI / 6),
      end.y - arrowSize * Math.sin(angle - Math.PI / 6)
    );
    this.ctx.lineTo(
      end.x - arrowSize * Math.cos(angle + Math.PI / 6),
      end.y - arrowSize * Math.sin(angle + Math.PI / 6)
    );
    this.ctx.closePath();
    this.ctx.fill();
    this.ctx.restore();

    // 繪製長度標籤
    const midX = (start.x + end.x) / 2;
    const midY = (start.y + end.y) / 2;
    const label = `${meters.toFixed(1)} m`;

    drawLabel(
      this.ctx,
      label,
      { x: midX, y: midY - 20 / this.scale },
      {
        textStyle: { color, size: 14 / this.scale, bold: true, align: 'center' },
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        padding: 4 / this.scale,
      }
    );
  }

  /**
   * 繪製預覽
   */
  private drawPreview(start: Point, end: Point): void {
    switch (this.drawingMode) {
      case 'reference':
        this.drawReferenceLinePreview(start, end);
        break;

      case 'eraser':
        this.drawEraserPreview(start, end);
        break;

      case 'blackline':
        this.drawBlackLinePreview(start, end);
        break;
    }
  }

  /**
   * 繪製參考線預覽
   */
  private drawReferenceLinePreview(start: Point, end: Point): void {
    const color = 'rgba(59, 130, 246, 0.7)';
    const lineWidth = 3 / this.scale;
    const dotRadius = 6 / this.scale;

    drawLine(this.ctx, start, end, {
      color,
      width: lineWidth,
      dash: [10 / this.scale, 5 / this.scale],
    });

    // 起點標記（綠色表示已確定）
    fillCircle(this.ctx, start, dotRadius, { color: '#22c55e' });
    // 終點標記（藍色表示待確定）
    fillCircle(this.ctx, end, dotRadius, { color });

    // 顯示像素長度
    const pixelLength = distance(start, end);
    const midX = (start.x + end.x) / 2;
    const midY = (start.y + end.y) / 2;

    drawLabel(
      this.ctx,
      `${Math.round(pixelLength)} px`,
      { x: midX, y: midY - 15 / this.scale },
      {
        textStyle: { color, size: 12 / this.scale, align: 'center' },
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        padding: 3 / this.scale,
      }
    );
  }

  /**
   * 繪製橡皮擦預覽
   */
  private drawEraserPreview(start: Point, end: Point): void {
    const rect: Rectangle = {
      x: Math.min(start.x, end.x),
      y: Math.min(start.y, end.y),
      width: Math.abs(end.x - start.x),
      height: Math.abs(end.y - start.y),
    };

    fillRect(this.ctx, rect, { color: 'rgba(255, 255, 255, 0.5)' });
    drawRect(this.ctx, rect, {
      color: '#ef4444',
      width: 2 / this.scale,
      dash: [5 / this.scale, 5 / this.scale],
    });
  }

  /**
   * 繪製塗黑線條預覽
   */
  private drawBlackLinePreview(start: Point, end: Point): void {
    const dotRadius = 6 / this.scale;

    // 繪製預覽線段
    drawLine(this.ctx, start, end, {
      color: 'rgba(0, 0, 0, 0.5)',
      width: 8 / this.scale,
      cap: 'round',
    });

    // 繪製起點標記（綠色圓點表示已確定的起點）
    fillCircle(this.ctx, start, dotRadius, { color: '#22c55e' });

    // 繪製終點標記（灰色圓點表示待確定的終點）
    fillCircle(this.ctx, end, dotRadius, { color: 'rgba(0, 0, 0, 0.5)' });
  }

  /**
   * 繪製偵測到的尺寸標註（包含線段和標籤）
   */
  private drawDetectedDimension(dim: DetectedDimension): void {
    const state = preprocessStore.getState();
    const isSelected = state.selectedDimensionId === dim.id;
    const isHovered = this.hoveredDimensionId === dim.id;
    const isInSelectMode = this.drawingMode === 'dimension-select';

    // 根據狀態決定顏色和線寬
    let color = '#059669'; // 預設綠色
    let lineWidth = 2 / this.scale;

    if (isSelected) {
      color = '#2563eb'; // 選中時藍色
      lineWidth = 3 / this.scale;
    } else if (isHovered && isInSelectMode) {
      color = '#10b981'; // 懸停時亮綠色
      lineWidth = 2.5 / this.scale;
    }

    // 繪製尺寸標註線段
    if (dim.line) {
      // 繪製主線段
      drawLine(this.ctx, dim.line.start, dim.line.end, {
        color,
        width: lineWidth,
      });

      // 繪製端點標記（小垂直線）
      const endMarkLength = 8 / this.scale;
      const dx = dim.line.end.x - dim.line.start.x;
      const dy = dim.line.end.y - dim.line.start.y;
      const length = Math.sqrt(dx * dx + dy * dy);

      if (length > 0) {
        // 單位向量（沿著線的方向）
        const ux = dx / length;
        const uy = dy / length;
        // 垂直向量
        const px = -uy;
        const py = ux;

        // 起點端點標記
        drawLine(
          this.ctx,
          { x: dim.line.start.x + px * endMarkLength, y: dim.line.start.y + py * endMarkLength },
          { x: dim.line.start.x - px * endMarkLength, y: dim.line.start.y - py * endMarkLength },
          { color, width: lineWidth }
        );

        // 終點端點標記
        drawLine(
          this.ctx,
          { x: dim.line.end.x + px * endMarkLength, y: dim.line.end.y + py * endMarkLength },
          { x: dim.line.end.x - px * endMarkLength, y: dim.line.end.y - py * endMarkLength },
          { color, width: lineWidth }
        );
      }

      // 如果選中，繪製拖曳把手
      if (isSelected) {
        this.drawDragHandles(dim.line.start, dim.line.end);
      }
    }

    // 繪製標籤
    const label = `${dim.value} ${dim.unit}`;
    const bgColor = isSelected ? 'rgba(219, 234, 254, 0.95)' : 'rgba(209, 250, 229, 0.9)';

    drawLabel(this.ctx, label, dim.position, {
      textStyle: {
        color,
        size: 12 / this.scale,
        bold: true,
        align: 'center',
      },
      backgroundColor: bgColor,
      padding: 4 / this.scale,
      borderRadius: 4 / this.scale,
    });
  }

  /**
   * 繪製拖曳把手
   */
  private drawDragHandles(start: Point, end: Point): void {
    const handleRadius = this.HANDLE_RADIUS / this.scale;
    const color = '#2563eb';
    const fillColor = '#ffffff';

    // 起點把手
    fillCircle(this.ctx, start, handleRadius, { color: fillColor });
    this.ctx.save();
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 2 / this.scale;
    this.ctx.beginPath();
    this.ctx.arc(start.x, start.y, handleRadius, 0, Math.PI * 2);
    this.ctx.stroke();
    this.ctx.restore();

    // 終點把手
    fillCircle(this.ctx, end, handleRadius, { color: fillColor });
    this.ctx.save();
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 2 / this.scale;
    this.ctx.beginPath();
    this.ctx.arc(end.x, end.y, handleRadius, 0, Math.PI * 2);
    this.ctx.stroke();
    this.ctx.restore();

    // 如果懸停在某個端點上，高亮顯示
    if (this.hoveredEndpoint) {
      const hoveredPoint = this.hoveredEndpoint === 'start' ? start : end;
      fillCircle(this.ctx, hoveredPoint, handleRadius * 1.2, { color: 'rgba(37, 99, 235, 0.3)' });
    }
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  /**
   * 設定繪圖模式
   */
  setMode(mode: DrawingMode): void {
    setDrawingMode(mode);
  }

  /**
   * 取得當前繪圖模式
   */
  getMode(): DrawingMode {
    return this.drawingMode;
  }

  /**
   * 產生處理後的圖片
   */
  generateProcessedImage(): string {
    if (!this.image) return '';

    const state = preprocessStore.getState();

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = this.image.width;
    tempCanvas.height = this.image.height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return '';

    // 繪製原始圖片
    tempCtx.drawImage(this.image, 0, 0);

    // 應用覆蓋矩形
    tempCtx.fillStyle = '#ffffff';
    for (const rect of state.coverRectangles) {
      tempCtx.fillRect(rect.x, rect.y, rect.width, rect.height);
    }

    // 應用塗黑線條
    tempCtx.strokeStyle = '#000000';
    tempCtx.lineWidth = 8;
    tempCtx.lineCap = 'round';
    for (const line of state.blackLines) {
      tempCtx.beginPath();
      tempCtx.moveTo(line.start.x, line.start.y);
      tempCtx.lineTo(line.end.x, line.end.y);
      tempCtx.stroke();
    }

    return tempCanvas.toDataURL('image/png');
  }

  // ========================================================================
  // 生命週期
  // ========================================================================

  destroy(): void {
    this.unsubscribe?.();
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
    super.destroy();
  }
}
